// İSG uzmanı portalı — İş İzni yaşam döngüsü aksiyonları (api-contract §14.2/§31.3/§33.1).
// Butonlar **duruma göre koşullu** gösterilir: Draft → Gönder (submit); Submitted → Onayla
// (approve) / Reddet (reject — gerekçe zorunlu); AwaitingAiReview → AI incelemesini yeniden
// çalıştır; AwaitingEntryPermit → Giriş izinlerini ver; DepartmentReview → Kontrol sayfasına
// bağlantı. Her aksiyon sonrası izin detayı + liste invalidate edilir; durum backend'in
// döndürdüğü değerdir (UI kendi hesaplamaz). Yanlış durumdan geçiş BE'de
// `409 PERMIT_INVALID_STATE` verir — butonlar zaten status'a göre gizli olduğundan UI'da
// tetiklenmez.
//
// **Carbon buton sırası + Reddet/Kapat stili (S33 planı §2.4, S34-1 A3 — web-ui-designer review
// M15 ile genişletildi).** Sıra soldan sağa: ghost(Edit, çağıran katmanda) ·
// `danger-tertiary`(Reddet) · `danger-tertiary`(Kapat) · primary(sıradaki akış adımı).
// `danger-tertiary` artık gerçek bir `Button` variant'ı (`packages/shared/src/ui/Button.tsx`,
// S34-1 A3) — Reddet + Kapat'ın (+ `DepartmentReviewActions.tsx`'in AYRI tetikleyicisi, bu
// dilimin kapsamı DIŞINDA, dokunulmadı) ortak className override'ının yerini alır. Dolu kırmızı
// yalnız onay diyaloglarının İÇİNDEKİ butonlarda (`RejectModal`'ın kendi `variant="danger"`ı,
// `ConfirmDialog`'un Kapat onayı). **Kapat artık PRIMARY DEĞİL** (S34-1 A3) — backend Kapat'ı
// Closed/Rejected DIŞINDA HER statüde kabul eder (`PermitsController.cs` `Close`), yani artık
// Submit/Approve/AI/Giriş izni gibi asıl akış adımlarıyla AYNI ANDA görünebilir; tertiary'ye
// indirilmesi ekranda ASLA iki dolu birincil buton olmamasını sağlar. Birincil (primary) buton
// HER ZAMAN "sıradaki akış adımı"dır ve statüler ayrık olduğundan (Draft/Submitted/
// AwaitingAiReview/AwaitingEntryPermit/DepartmentReview birbirini dışlar) aynı anda en fazla BİR
// primary render edilir; diğer statülerde (AwaitingSupplierInfo/Approved, Revision, Active,
// terminal) primary YOKTUR.
// **İki kırmızı çerçeveli buton asla yan yana değil (review M15).** Reddet + Kapat yalnız
// `Submitted`'ta ÇAKIŞIR (Reddet yalnız Submitted'ta, Kapat Closed/Rejected DIŞINDA HER yerde) —
// bu TEK statüde Kapat standalone buton DEĞİL, `RowActionsMenu`'nün (⋮, S33 Slice 4 emsali)
// tek-öğeli bir örneğine taşınır; Reddet birincil kırmızı eylem olarak sayfada kalır.
//
// **Reviewer görünürlük sinyali (api-contract §30.4).** `canReview` = çağıran reviewer mı
// (`permits/approve` grant'ı, statüden BAĞIMSIZ); `canApprove` = `canReview` VE izin
// `Submitted` (yalnız onay/ret'e özgü, bu yüzden AI/Giriş izni butonları İÇİN kullanılamaz).
// AI yeniden-tetik + Giriş izni ver aynı `permits/approve` kapısını kullandığından (§31.3/§33.1)
// `canReview` ile aynı anti-403 desenine bağlanır: sinyal VARSA `canReview`'a, YOKSA (eski
// davranış / alan yoksa) yalnız statüye düşülür — regresyon yok.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  RowActionsMenu,
  Spinner,
  type WorkPermitDto,
  permitsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, KeyRound, Lock, RefreshCw, Send, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { permitsKeys } from '../../lib/permitsKeys';

interface PermitLifecycleActionsProps {
  permit: WorkPermitDto;
  /**
   * Reviewer sinyalleri — api-contract §30.4 (`WorkPermitDetailDto.canApprove/canReview`; brief 098
   * İş 3). Verildiğinde onay/ret buton görünürlüğü 403 yerine bu alanlara bağlanır (backend yetki
   * otoritesidir; buton yalnız UX). **Verilmezse** (eski davranış / alan yoksa) status-bazlı gate'e
   * düşülür — regresyon yok.
   */
  canApprove?: boolean;
  canReview?: boolean;
}

type ConfirmKind = 'submit' | 'approve' | 'close' | 'rerunAiReview' | 'issueEntryPermits' | null;

export function PermitLifecycleActions({
  permit,
  canApprove,
  canReview,
}: PermitLifecycleActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [rejectOpen, setRejectOpen] = useState(false);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permit.uuid) });
    queryClient.invalidateQueries({ queryKey: permitsKeys.list() });
  }

  const submitMutation = useMutation({
    mutationFn: () => permitsApi.submitPermit(permit.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const approveMutation = useMutation({
    mutationFn: () => permitsApi.approvePermit(permit.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const closeMutation = useMutation({
    mutationFn: () => permitsApi.closePermit(permit.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const rerunAiReviewMutation = useMutation({
    mutationFn: () => permitsApi.rerunAiReview(permit.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });
  const issueEntryPermitsMutation = useMutation({
    mutationFn: () => permitsApi.issueEntryPermits(permit.uuid),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
  });

  const status = permit.status;
  const canSubmit = status === 'Draft';
  // Onay/Ret görünürlüğü: reviewer sinyali (§30.4) VARSA ona bağlanır (backend: `canApprove` =
  // reviewer VE `Submitted`); yoksa status-bazlı eski davranışa düşülür (regresyon yok). `canReview`
  // sinyali gelmiş ama `canApprove` false ise (reviewer ama izin `Submitted` değil) butonlar gizli
  // kalır — yetkisiz kullanıcıya 403 gösterilmez.
  const hasReviewSignal = canApprove !== undefined || canReview !== undefined;
  const canApproveReject = hasReviewSignal ? Boolean(canApprove) : status === 'Submitted';
  // Reddet: yalnız `Submitted` (S34-1 A3 fix — Draft eskiden fallback dalında YANLIŞLIKLA
  // Reddet gösteriyordu; backend `reject` zaten yalnız Draft/Submitted'ı kabul eder ama
  // Draft'ta doğru aksiyon "Gönder"i geri almak DEĞİL, izni hiç göndermemektir).
  const canReject = hasReviewSignal ? Boolean(canApprove) : status === 'Submitted';
  // Kapat artık PRIMARY DEĞİL, tertiary/danger (S34-1 A3 — dosya başlığı) — backend `close`'u
  // yalnız `Closed`/`Rejected` DIŞINDAKİ statülerde kabul eder (`PermitsController.cs` `Close`,
  // api-contract §14.2), reviewer sinyaline bağlı DEĞİLDİR (herhangi bir `update` yetkilisi
  // kapatabilir).
  const canClose = status !== 'Closed' && status !== 'Rejected';
  // İki kırmızı-çerçeveli buton asla yan yana değil (review M15) — Reddet + Kapat yalnız
  // `Submitted`'ta ÇAKIŞIR (bkz. dosya başlığı). O TEK durumda Kapat standalone buton DEĞİL,
  // `RowActionsMenu`'nün tek-öğeli bir örneğine taşınır.
  const closeSharesRowWithReject = canClose && canReject;
  // AI yeniden-tetik / Giriş izni ver — aynı `permits/approve` kapısı (§31.3/§33.1), dosya
  // başlığındaki reviewer-sinyali notuna göre `canReview`'a bağlanır (statüden BAĞIMSIZ sinyal;
  // `canApprove` yalnız Submitted'a özgü olduğundan burada KULLANILAMAZ).
  const canRerunAiReview =
    status === 'AwaitingAiReview' && (hasReviewSignal ? Boolean(canReview) : true);
  const canIssueEntryPermits =
    status === 'AwaitingEntryPermit' && (hasReviewSignal ? Boolean(canReview) : true);
  const showDepartmentReviewLink = status === 'DepartmentReview';

  const activeConfirm = useMemo(() => {
    switch (confirm) {
      case 'submit':
        return { mutation: submitMutation, variant: 'primary' as const };
      case 'approve':
        return { mutation: approveMutation, variant: 'primary' as const };
      case 'close':
        return { mutation: closeMutation, variant: 'danger' as const };
      case 'rerunAiReview':
        return { mutation: rerunAiReviewMutation, variant: 'primary' as const };
      case 'issueEntryPermits':
        return { mutation: issueEntryPermitsMutation, variant: 'primary' as const };
      default:
        return null;
    }
  }, [
    confirm,
    submitMutation,
    approveMutation,
    closeMutation,
    rerunAiReviewMutation,
    issueEntryPermitsMutation,
  ]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canReject ? (
        // Sayfa düzeyinde kırmızı ÇERÇEVE — dolu kırmızı yalnız aşağıdaki `RejectModal`'ın kendi
        // onay butonunda (`variant="danger"`, dokunulmadı).
        <Button variant="danger-tertiary" onClick={() => setRejectOpen(true)}>
          <XCircle className="h-4 w-4" aria-hidden="true" />
          {t('permits.lifecycle.reject')}
        </Button>
      ) : null}
      {canClose && !closeSharesRowWithReject ? (
        // Kapat artık primary DEĞİL, akışın asıl "sıradaki adım" butonuyla AYNI ANDA görünebilir.
        <Button variant="danger-tertiary" onClick={() => setConfirm('close')}>
          <Lock className="h-4 w-4" aria-hidden="true" />
          {t('permits.lifecycle.close')}
        </Button>
      ) : null}
      {closeSharesRowWithReject ? (
        // Reddet'le AYNI ANDA görünen tek durum (`Submitted`) — Kapat standalone kırmızı buton
        // olarak DEĞİL, tek-öğeli ⋮ menüsünde (review M15, `RowActionsMenu` emsali).
        <RowActionsMenu
          ariaLabel={t('permits.lifecycle.action.openMenu', { name: permit.title })}
          items={[
            {
              key: 'close',
              label: t('permits.lifecycle.close'),
              onSelect: () => setConfirm('close'),
              destructive: true,
            },
          ]}
        />
      ) : null}
      {canSubmit ? (
        <Button variant="primary" onClick={() => setConfirm('submit')}>
          <Send className="h-4 w-4" aria-hidden="true" />
          {t('permits.lifecycle.submit')}
        </Button>
      ) : null}
      {canApproveReject ? (
        <Button variant="primary" onClick={() => setConfirm('approve')}>
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          {t('permits.lifecycle.approve')}
        </Button>
      ) : null}
      {canRerunAiReview ? (
        <Button variant="primary" onClick={() => setConfirm('rerunAiReview')}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {t('permits.lifecycle.rerunAiReview')}
        </Button>
      ) : null}
      {canIssueEntryPermits ? (
        <Button variant="primary" onClick={() => setConfirm('issueEntryPermits')}>
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          {t('permits.lifecycle.issueEntryPermits')}
        </Button>
      ) : null}
      {showDepartmentReviewLink ? (
        <Button variant="primary" asChild>
          <Link to={`/department-review/${permit.uuid}`}>
            {t('permits.lifecycle.goToReview')}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      ) : null}

      {activeConfirm ? (
        <ConfirmDialog
          open={confirm !== null}
          onOpenChange={(open) => !open && setConfirm(null)}
          title={t(`permits.lifecycle.${confirm}.title`)}
          body={t(`permits.lifecycle.${confirm}.body`)}
          confirmLabel={t(`permits.lifecycle.${confirm}.confirm`)}
          confirmVariant={activeConfirm.variant}
          loading={activeConfirm.mutation.isPending}
          error={activeConfirm.mutation.isError ? activeConfirm.mutation.error : undefined}
          onConfirm={() => activeConfirm.mutation.mutate()}
        />
      ) : null}

      <RejectModal
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        permitUuid={permit.uuid}
        onDone={invalidate}
      />
    </div>
  );
}

interface RejectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permitUuid: string;
  onDone: () => void;
}

function RejectModal({ open, onOpenChange, permitUuid, onDone }: RejectModalProps) {
  const { t } = useTranslation();

  const schema = useMemo(
    () => z.object({ reason: z.string().trim().min(1, t('permits.validation.reasonRequired')) }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      permitsApi.rejectPermit(permitUuid, { reason: values.reason.trim() }),
    onSuccess: () => {
      onDone();
      reset();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={t('permits.reject.title')}
      description={t('permits.reject.description')}
      size="sm"
      closeLabel={t('permits.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permits.common.cancel')}
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="permit-reject-form"
            loading={mutation.isPending}
          >
            {mutation.isPending ? (
              <Spinner label={t('permits.reject.confirm')} />
            ) : (
              t('permits.reject.confirm')
            )}
          </Button>
        </>
      }
    >
      <form
        id="permit-reject-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}
        <Field
          htmlFor="permit-reject-reason"
          label={t('permits.reject.field.reason')}
          error={formState.errors.reason?.message}
          required
        >
          <Input
            id="permit-reject-reason"
            invalid={Boolean(formState.errors.reason)}
            placeholder={t('permits.reject.reasonPlaceholder')}
            {...register('reason')}
          />
        </Field>
      </form>
    </Modal>
  );
}
